import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { UserPlus, Edit, Trash2, Eye, Phone, MapPin, Globe as GlobeIcon, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const Clients = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'client',
    phone: '',
    phone_secondary: '',
    address: '',
    country_origin: '',
    nationality: '',
    file_type: '',
    date_of_birth: '',
    passport_number: '',
    passport_expiry: '',
    marital_status: '',
    number_of_children: '',
    profession: '',
    education_level: '',
    languages: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: ''
  });
  const [newlyCreatedClient, setNewlyCreatedClient] = useState(null); // Store newly created client info

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fileTypes = [
    'Consultation initial',
    'Entrée express',
    'Programme des candidats des provinces (PCP / PNP)',
    'Programmes pilotes fédéraux',
    'Programmes d\'immigration du Québec (PRTQ, PEQ, Programmes pilotes)',
    'Immigration de l\'Atlantique',
    'Immigration rurale et du Nord',
    'Parrainage d\'un époux(se) ou conjoint(e) de fait',
    'Parrainage d\'un enfant à charge',
    'Parrainage d\'un parent ou grand-parent',
    'Parrainage pour motif humanitaire (membre de la famille orphelin, etc.)',
    'Demande d\'asile',
    'Réinstallation de réfugiés à l\'extérieur du Canada',
    'Appel devant la Section d\'appel des réfugiés (SAR / RAD)',
    'Demande pour considérations d\'ordre humanitaire (H&C)',
    'Permis de séjour temporaire (PST)',
    'Permis d\'études',
    'CAQ pour études (Québec)',
    'Permis de travail fermé',
    'Permis de travail ouvert',
    'Permis de travail postdiplôme (PTPD)',
    'Permis pour travailleur vulnérable',
    'Permis de travail avec ou sans EIMT',
    'Visa de visiteur',
    'Super visa (parents/grands-parents)',
    'Autorisation de voyage électronique (AVE)',
    'Prorogation ou rétablissement de statut',
    'Demande de citoyenneté',
    'Rétablissement de la citoyenneté',
    'Preuve ou certificat de citoyenneté',
    'Renonciation à la citoyenneté',
    'Appel devant la Section d\'appel de l\'immigration (SAI / IAD)',
    'Appel devant la Section d\'appel des réfugiés (SAR / RAD)',
    'Reconsidération ou révision d\'une décision d\'IRCC',
    'Consultation en immigration d\'affaires et recrutement international',
    'Formation et accompagnement pour employeurs (EIMT, offre d\'emploi, Portail des employeurs)',
    'EIMT',
    'Offre d\'emploi valide',
    'Formations et accompagnement en conformité'
  ];

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    // Show download dialog after client creation
    if (newlyCreatedClient) {
      setDownloadDialogOpen(true);
    }
  }, [newlyCreatedClient]);

  const handleDownloadConfirm = () => {
    if (newlyCreatedClient) {
      downloadClientCredentials(newlyCreatedClient.id, newlyCreatedClient.name, newlyCreatedClient.password);
    }
    setDownloadDialogOpen(false);
  };

  const handleDownloadCancel = () => {
    setNewlyCreatedClient(null);
    setDownloadDialogOpen(false);
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        const clientsList = response.data.filter(u => u.role === 'client');
        setClients(clientsList);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(t('error'));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(
          `${API}/users/${editingClient.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Client modifié avec succès');
      } else {
        // Prepare metadata for client
        const clientData = {
          ...formData,
          metadata: {
            date_of_birth: formData.date_of_birth || undefined,
            passport_number: formData.passport_number || undefined,
            passport_expiry: formData.passport_expiry || undefined,
            marital_status: formData.marital_status || undefined,
            number_of_children: formData.number_of_children || undefined,
            profession: formData.profession || undefined,
            education_level: formData.education_level || undefined,
            languages: formData.languages || undefined,
            nationality: formData.nationality || undefined,
            phone_secondary: formData.phone_secondary || undefined,
            emergency_contact_name: formData.emergency_contact_name || undefined,
            emergency_contact_phone: formData.emergency_contact_phone || undefined,
            emergency_contact_relation: formData.emergency_contact_relation || undefined
          }
        };
        
        const response = await axios.post(
          `${API}/auth/register`,
          clientData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Store newly created client info with password for PDF generation
        setNewlyCreatedClient({
          id: response.data.id,
          name: formData.full_name,
          password: formData.password
        });
        
        toast.success('Client créé avec succès! Voulez-vous télécharger le PDF avec les identifiants?');
      }
      setDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    
    try {
      await axios.delete(`${API}/users/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setFormData({
      full_name: client.full_name,
      email: client.email,
      password: '',
      role: 'client',
      phone: client.phone || '',
      phone_secondary: client.metadata?.phone_secondary || '',
      address: client.address || '',
      country_origin: client.country_origin || '',
      nationality: client.metadata?.nationality || '',
      file_type: client.file_type || '',
      date_of_birth: client.metadata?.date_of_birth || '',
      passport_number: client.metadata?.passport_number || '',
      passport_expiry: client.metadata?.passport_expiry || '',
      marital_status: client.metadata?.marital_status || '',
      number_of_children: client.metadata?.number_of_children || '',
      profession: client.metadata?.profession || '',
      education_level: client.metadata?.education_level || '',
      languages: client.metadata?.languages || '',
      emergency_contact_name: client.metadata?.emergency_contact_name || '',
      emergency_contact_phone: client.metadata?.emergency_contact_phone || '',
      emergency_contact_relation: client.metadata?.emergency_contact_relation || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'client',
      phone: '',
      phone_secondary: '',
      address: '',
      country_origin: '',
      nationality: '',
      file_type: '',
      date_of_birth: '',
      passport_number: '',
      passport_expiry: '',
      marital_status: '',
      number_of_children: '',
      profession: '',
      education_level: '',
      languages: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relation: ''
    });
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({ ...formData, password });
    toast.success('Mot de passe généré : ' + password, { duration: 10000 });
    // Copy to clipboard
    navigator.clipboard.writeText(password);
    toast.info('Mot de passe copié dans le presse-papiers');
  };

  const downloadClientCredentials = async (clientId, clientName, providedPassword = null) => {
    try {
      toast.info('Génération des identifiants en cours...');
      
      const response = await axios.post(
        `${API}/users/${clientId}/download-credentials`,
        null,
        {
          params: providedPassword ? { password: providedPassword } : {},
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Identifiants_${clientName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      if (providedPassword) {
        toast.success('📄 Identifiants générés avec le mot de passe défini!');
      } else {
        toast.success('📄 Identifiants générés ! Un nouveau mot de passe temporaire a été créé.');
        toast.warning('⚠️ Le client devra changer son mot de passe à la première connexion.', { duration: 5000 });
      }
      
      // Clear newly created client state after download
      setNewlyCreatedClient(null);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Vous pouvez uniquement télécharger les identifiants des clients qui vous sont assignés');
      } else {
        toast.error('Erreur lors de la génération des identifiants');
      }
      console.error('Error downloading credentials:', error);
    }
  };

  const downloadClientInfo = async (clientId, clientName) => {
    try {
      toast.info('Génération du dossier client en cours...');
      
      const response = await axios.post(
        `${API}/users/${clientId}/download-client-info`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dossier_Client_${clientName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('📄 Dossier client téléchargé avec succès!');
    } catch (error) {
      toast.error('Erreur lors de la génération du dossier client');
      console.error('Error downloading client info:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="clients-container">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {t('clients')}
            </h1>
            <p className="text-lg text-gray-600">
              {clients.length} {clients.length === 1 ? 'client' : 'clients'}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 shadow-lg"
                data-testid="add-client-btn"
              >
                <UserPlus className="mr-2 h-5 w-5" />
                Ajouter un client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingClient ? 'Modifier le client' : 'Ajouter un client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Section: Informations de base */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations de base</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('full_name')} *</Label>
                      <Input
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                        placeholder="Nom complet"
                        data-testid="client-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('email')} *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        disabled={editingClient}
                        placeholder="email@exemple.com"
                        data-testid="client-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>{t('password')} {editingClient && '(laisser vide pour ne pas changer)'} {!editingClient && '*'}</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={generatePassword}
                          className="text-xs"
                        >
                          Générer
                        </Button>
                      </div>
                      <Input
                        type="text"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required={!editingClient}
                        placeholder="Mot de passe"
                        data-testid="client-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de naissance</Label>
                      <Input
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Coordonnées */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Coordonnées</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Téléphone principal</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+1 (514) 123-4567"
                        data-testid="client-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone secondaire</Label>
                      <Input
                        type="tel"
                        value={formData.phone_secondary}
                        onChange={(e) => setFormData({ ...formData, phone_secondary: e.target.value })}
                        placeholder="+1 (514) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Adresse complète</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="123 Rue Exemple, Montréal, QC, H1A 1A1"
                      data-testid="client-address-input"
                    />
                  </div>
                </div>

                {/* Section: Informations d'immigration */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations d'immigration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pays d'origine</Label>
                      <Input
                        value={formData.country_origin}
                        onChange={(e) => setFormData({ ...formData, country_origin: e.target.value })}
                        placeholder="France, Maroc, Algérie, etc."
                        data-testid="client-country-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nationalité</Label>
                      <Input
                        value={formData.nationality}
                        onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                        placeholder="Française, Marocaine, etc."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Numéro de passeport</Label>
                      <Input
                        value={formData.passport_number}
                        onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                        placeholder="12AB34567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date d'expiration du passeport</Label>
                      <Input
                        type="date"
                        value={formData.passport_expiry}
                        onChange={(e) => setFormData({ ...formData, passport_expiry: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Type de dossier</Label>
                    <Select
                      value={formData.file_type}
                      onValueChange={(value) => setFormData({ ...formData, file_type: value })}
                    >
                      <SelectTrigger data-testid="client-filetype-select">
                        <SelectValue placeholder="Sélectionner un type de dossier" />
                      </SelectTrigger>
                      <SelectContent>
                        {fileTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Section: Informations personnelles */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Informations personnelles</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Statut matrimonial</Label>
                      <Select
                        value={formData.marital_status}
                        onValueChange={(value) => setFormData({ ...formData, marital_status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="celibataire">Célibataire</SelectItem>
                          <SelectItem value="marie">Marié(e)</SelectItem>
                          <SelectItem value="conjoint_de_fait">Conjoint de fait</SelectItem>
                          <SelectItem value="divorce">Divorcé(e)</SelectItem>
                          <SelectItem value="veuf">Veuf/Veuve</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre d'enfants</Label>
                      <Input
                        type="number"
                        value={formData.number_of_children}
                        onChange={(e) => setFormData({ ...formData, number_of_children: e.target.value })}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>Profession</Label>
                      <Input
                        value={formData.profession}
                        onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                        placeholder="Ingénieur, Médecin, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Niveau d'éducation</Label>
                      <Select
                        value={formData.education_level}
                        onValueChange={(value) => setFormData({ ...formData, education_level: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="secondaire">Secondaire</SelectItem>
                          <SelectItem value="collegial">Collégial/CEGEP</SelectItem>
                          <SelectItem value="baccalaureat">Baccalauréat</SelectItem>
                          <SelectItem value="maitrise">Maîtrise</SelectItem>
                          <SelectItem value="doctorat">Doctorat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Langues parlées</Label>
                    <Input
                      value={formData.languages}
                      onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
                      placeholder="Français, Anglais, Arabe, etc."
                    />
                  </div>
                </div>

                {/* Section: Contact d'urgence */}
                <div className="pb-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact d'urgence</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom du contact</Label>
                      <Input
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        placeholder="Nom du contact d'urgence"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Téléphone du contact</Label>
                      <Input
                        type="tel"
                        value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                        placeholder="+1 (514) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <Label>Lien de parenté</Label>
                    <Input
                      value={formData.emergency_contact_relation}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_relation: e.target.value })}
                      placeholder="Époux/Épouse, Parent, Frère/Sœur, etc."
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700"
                  data-testid="submit-client-btn"
                >
                  {editingClient ? 'Modifier le client' : 'Créer le client'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Nom</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Téléphone</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Pays</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Créé le</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {clients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50" data-testid={`client-row-${client.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {client.full_name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {client.email}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {client.phone && <Phone className="h-3 w-3" />}
                          {client.phone || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          {client.country_origin && <GlobeIcon className="h-3 w-3" />}
                          {client.country_origin || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {client.file_type ? (
                          <Badge variant="outline">{client.file_type}</Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {format(new Date(client.created_at), 'dd/MM/yyyy')}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/files/${client.id}`)}
                            data-testid={`view-client-${client.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(client)}
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          
                          {/* Bouton Identifiants - pour tous sauf client */}
                          {user.role !== 'client' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadClientCredentials(client.id, client.full_name)}
                              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 flex items-center gap-1"
                              title="Télécharger les identifiants de connexion"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              <span className="hidden xl:inline">Identifiants</span>
                            </Button>
                          )}
                          
                          {/* Bouton Dossier complet - pour admin et employés */}
                          {user.role !== 'client' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadClientInfo(client.id, client.full_name)}
                              className="bg-gradient-to-r from-green-600 to-teal-600 text-white hover:from-green-700 hover:to-teal-700 flex items-center gap-1"
                              title="Télécharger le dossier complet du client"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="hidden xl:inline">Dossier</span>
                            </Button>
                          )}
                          
                          {/* Bouton Supprimer - uniquement pour admin et subadmin */}
                          {(user.role === 'admin' || user.role === 'subadmin') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(client.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {clients.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  <UserPlus className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Aucun client</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Download Credentials Dialog */}
      <Dialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Download className="h-5 w-5" />
              Client créé avec succès !
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="space-y-4">
                <p className="text-gray-700">
                  Voulez-vous télécharger le PDF avec les identifiants de connexion maintenant ?
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <strong>Important :</strong> C&apos;est la seule fois où vous pourrez télécharger le PDF avec le mot de passe d&apos;origine. 
                    Sinon, un nouveau mot de passe temporaire sera généré.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleDownloadCancel}>
              Plus tard
            </Button>
            <Button 
              onClick={handleDownloadConfirm}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Clients;